import { useEffect, useLayoutEffect, useState } from 'react';
import { type Rect } from './types';
import { TOUR_HOST_TAG_NAME } from './topLayerPortal';

const TOUR_CARD_SELECTOR = `${TOUR_HOST_TAG_NAME} [role="dialog"]`;

/**
 * The target's own `border-radius`, as the multi-value string it computes to
 * (`"16px 16px 0px 0px"` for a drawer rounded along its top edge only). Read
 * once per element: unlike the rect, it does not change on scroll or resize.
 */
export const useElementBorderRadius = (
  element: HTMLElement | null
): string | null => {
  const [borderRadius, setBorderRadius] = useState<string | null>(null);

  useLayoutEffect(() => {
    setBorderRadius(
      element ? window.getComputedStyle(element).borderRadius : null
    );
  }, [element]);

  return borderRadius;
};

// How often the tracking poll measures. What moves an anchor is a column
// autosize or a surface sliding in, and the highlight has `transition: none`
// while tracking, so measuring every fourth frame costs latency the eye does
// not catch and spares three quarters of the forced layouts.
const POLL_INTERVAL_MS = 60;

const measureRect = (element: HTMLElement): Rect => {
  const { top, left, width, height } = element.getBoundingClientRect();
  return { top, left, width, height };
};

const RECT_EPSILON_PX = 0.5;

export const areRectsEqual = (a: Rect, b: Rect): boolean =>
  Math.abs(a.top - b.top) < RECT_EPSILON_PX &&
  Math.abs(a.left - b.left) < RECT_EPSILON_PX &&
  Math.abs(a.width - b.width) < RECT_EPSILON_PX &&
  Math.abs(a.height - b.height) < RECT_EPSILON_PX;

/**
 * Tracks a target's viewport rect, re-measuring on scroll and resize.
 *
 * `trackMovingAnchor` additionally polls the rect, at `POLL_INTERVAL_MS`, for
 * as long as the step is anchored. Neither `ResizeObserver` nor scroll/resize
 * reports an element that is *moved* rather than resized — by an ancestor
 * animating or reflowing it into place (a drawer sliding open), or by a sibling
 * growing and pushing it sideways (an AG Grid pinned column widening on
 * autosize) — and such a move leaves the highlight the right size in the wrong
 * place.
 */
export const useElementRect = (
  element: HTMLElement | null,
  trackMovingAnchor: boolean
): Rect | null => {
  const [rect, setRect] = useState<Rect | null>(null);

  useLayoutEffect(() => {
    if (!element) {
      setRect(null);
      return;
    }

    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setRect(measureRect(element)));
    };

    const initial = measureRect(element);
    setRect(initial);

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    let pollFrame = 0;
    if (trackMovingAnchor) {
      let previous = initial;
      let measuredAt = performance.now();
      const poll = () => {
        pollFrame = requestAnimationFrame(poll);
        const now = performance.now();
        if (now - measuredAt < POLL_INTERVAL_MS) return;
        measuredAt = now;
        const next = measureRect(element);
        if (!areRectsEqual(next, previous)) {
          previous = next;
          setRect(next);
        }
      };
      pollFrame = requestAnimationFrame(poll);
    }

    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(pollFrame);
      resizeObserver.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [element, trackMovingAnchor]);

  return rect;
};

const SWALLOWED_EVENTS = [
  'pointerdown',
  'mousedown',
  'mouseup',
  'click',
  'dblclick',
] as const;

/**
 * Swallow clicks on the page while a step is showing, without blocking hover.
 * The backdrop already passes pointer events through for `allowInteraction`
 * steps; this keeps the hover-driven affordances working while stopping a click
 * from navigating the user out of the tour. Events on the tour card pass
 * through so Back/Next/Close keep working.
 */
export const useBlockPageClicks = (isActive: boolean): void => {
  useEffect(() => {
    if (!isActive) return;
    const swallow = (event: Event) => {
      const target = event.target;
      if (target instanceof Element && target.closest(TOUR_CARD_SELECTOR)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    };
    SWALLOWED_EVENTS.forEach(eventName =>
      document.addEventListener(eventName, swallow, true)
    );
    return () =>
      SWALLOWED_EVENTS.forEach(eventName =>
        document.removeEventListener(eventName, swallow, true)
      );
  }, [isActive]);
};
